import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Text } from '@/shared/ui';
import type { Visit } from '@services/requests/housien-models/Visit';
import { SectionCard } from './VisitDetailPrimitives';
import { PropertyCard, ClientCard, BrokerCard, NotesCard } from './VisitDetailCards';
import { VisitAttendanceCard } from './VisitAttendanceCard';
import { useIsSoloBroker } from '@/shared/hooks/use-is-solo-broker';

interface VisitDetailBodyProps {
  visit: Visit;
  agencyId: string;
}

/**
 * Body content below the hero card and action bar. Two-column layout:
 * left (property + notes), right (client + broker + participants).
 */
export function VisitDetailBody({ visit, agencyId }: VisitDetailBodyProps) {
  const { t } = useTranslation('@pages/agencies');
  const navigate = useNavigate();
  const isSoloBroker = useIsSoloBroker();
  const prop = visit.properties?.[0];
  const participants = visit.participants ?? [];

  return (
    <div className="grid grid-cols-1 gap-[var(--space-4)] lg:grid-cols-2">
      <div className="flex flex-col gap-[var(--space-4)]">
        <PropertyCard
          prop={prop}
          propertyIds={visit.property_ids}
          onNavigate={(id) => navigate(`/app/agencies/${agencyId}/properties/${id}`)}
          className="flex-1"
        />
        {visit.notes && <NotesCard notes={visit.notes} />}
      </div>
      <div className="flex flex-col gap-[var(--space-4)]">
        <ClientCard
          clientName={visit.client_name}
          clientPhone={visit.client_phone}
          clientId={visit.client_id}
          onNavigate={(id) => navigate(`/app/agencies/${agencyId}/clients/${id}`)}
        />
        {!isSoloBroker && (
          <BrokerCard brokerName={visit.broker_name} ownerEmail={visit.owner_email} className="flex-1" />
        )}
        <VisitAttendanceCard agencyId={agencyId} dealId={visit.deal_id} />
        {participants.length > 0 && (
          <SectionCard title={t('visits.detail.invitedParticipants', 'Participantes convidados')}>
            <div className="flex flex-col gap-[var(--space-3)]">
              {participants.map((p: Record<string, unknown>, i: number) => (
                <ParticipantRow key={i} participant={p} />
              ))}
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}

interface ParticipantRowProps {
  participant: Record<string, unknown>;
}

/**
 * Single participant row showing email and invitation status.
 */
function ParticipantRow({ participant }: ParticipantRowProps) {
  const email = (participant.email as string) || '—';
  const status = (participant.invitation_status as string) || 'pending';
  const statusColor = status === 'accepted'
    ? 'text-[#3D5A47]'
    : status === 'rejected'
      ? 'text-[#7A3B1E]'
      : 'text-(--text-tertiary)';

  return (
    <div className="flex items-center justify-between">
      <Text className="text-sm text-(--text-primary) truncate">{email}</Text>
      <Text className={`text-[length:var(--text-meta)] font-medium ${statusColor} capitalize`}>
        {status}
      </Text>
    </div>
  );
}
